// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: e1b3359f-ba4e-4360-8bca-fafbfe3c744a

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, Button, Chip, Alert, Divider, Grid,
  Collapse, IconButton, Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress, Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement,
  Tooltip as ChartTooltip, Legend, Title,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { PICKLISTS } from './schema';
import store, { computeCompletion } from './store';
import { getConfig } from './config';
import { getIdToken } from './auth';
import { exportCsv, printElementToPdf } from './exports';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, ChartTooltip, Legend, Title);

// ---------------------------------------------------------------------------
// SNAPSHOT COMPARE — "what have we actually moved since kickoff?"
//
// An Optimum engagement runs for months. The steering committee asks that
// question every time, and until now the honest answer was "we don't know" —
// the CMDB only ever shows *today*. The audit trail (backend/sql/005_audit.sql)
// makes an answer possible: every INSERT / UPDATE / DELETE is logged with a
// {field: {from, to}} diff, so today's state plus the log is enough to
// reconstruct any earlier state.
//
// HOW THE RECONSTRUCTION WORKS (this is the subtle part)
// -----------------------------------------------------
// We do NOT replay forwards from an empty portfolio — there is no "beginning of
// time" snapshot to start from, and the audit trail only goes back to the day
// the triggers were installed. Instead we start from the CURRENT state (which
// we know is correct) and REVERSE every audit entry newer than the target date,
// walking newest -> oldest:
//
//   UPDATE  -> for each changed field, write `changes[field].from` back.
//   INSERT  -> the fact sheet did not exist yet, so remove it.
//   DELETE  -> the fact sheet still existed, so put it back.
//
// Order matters: reversing must run strictly newest-first, otherwise an older
// `from` value gets overwritten by a newer one. An app that was created AND
// deleted inside the window ends up correctly absent, because we re-add it at
// its DELETE and then remove it again at its INSERT.
//
// HONEST LIMITATIONS — say these out loud in the readout, do not hide them:
//   1. Nothing before the audit trail existed can be reconstructed. If a field
//      was last changed before the first trigger fired, the reverse-replay has
//      no entry to undo and the "From" snapshot simply carries today's value.
//      The From date is therefore a floor, never earlier than the first entry.
//   2. The DELETE trigger stores an empty `changes` object (it records that the
//      row went away, not what was in it). A re-added fact sheet is therefore a
//      NAME-ONLY STUB: it counts correctly in "applications retired", but its
//      old disposition / wave / completion are unknown and are not asserted.
//   3. The API returns at most 200 audit rows (see backend/lambda/index.js). On
//      a busy tenant an older window may be only partially reconstructable; we
//      surface the age of the oldest entry we actually received so nobody reads
//      more into the numbers than is there.
// ---------------------------------------------------------------------------

// Optimum palette.
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const GREEN = '#2E7D32';
const AMBER = '#B26A00';
const RED = '#C62828';

const PRINT_ID = 'snapshot-compare-print';

// Fact-sheet columns that live outside the flexible `data` jsonb bag.
const TOP_LEVEL_KEYS = { type: 1, quality: 1, tenantId: 1 };

// Disposition fields a steering committee actually argues about.
const DISPOSITION_FIELDS = [
  { key: 'sixRClassification', label: '6R' },
  { key: 'cmDisposition', label: 'CM Disposition' },
  { key: 'timeClassification', label: 'TIME' },
];

const OWNERSHIP_FIELDS = [
  { key: 'applicationOwner', label: 'Business Owner' },
  { key: 'technicalOwner', label: 'Technical Owner' },
  { key: 'primaryStakeholder', label: 'Primary Stakeholder' },
];

const WAVES = PICKLISTS.migrationWave || ['Wave 1', 'Wave 2', 'Wave 3', 'Not Scheduled'];
const DONUT_COLORS = [
  '#0D5B9A', '#021F4B', '#2E7D32', '#F5A623', '#8E44AD', '#00838F',
  '#C62828', '#5C6B7A', '#7CB342', '#D81B60',
];

// ---- small date helpers ---------------------------------------------------
const pad = (n) => String(n).padStart(2, '0');
const toInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };

// A snapshot "as of" a day means the end of that day, local time.
function endOfDayMs(value) {
  if (!value) return NaN;
  const parts = String(value).split('-').map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => isNaN(n))) return NaN;
  return new Date(parts[0], parts[1] - 1, parts[2], 23, 59, 59, 999).getTime();
}

function longDate(value, withYear) {
  const ms = endOfDayMs(value);
  if (isNaN(ms)) return String(value || '');
  return new Date(ms).toLocaleDateString(undefined,
    withYear ? { day: 'numeric', month: 'long', year: 'numeric' } : { day: 'numeric', month: 'long' });
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// ", " between, ", and " before the last — reads correctly when pasted into a
// status report.
function sentenceList(parts) {
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

const camelize = (s) => String(s).replace(/_([a-z0-9])/g, (m, c) => c.toUpperCase());

function displayValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') { try { return JSON.stringify(v); } catch (e) { return String(v); } }
  return String(v);
}

// ---- audit-row normalisation (same shapes HistoryTab accepts) -------------
function normalizeEntry(raw, index) {
  const pick = (...keys) => {
    for (let i = 0; i < keys.length; i += 1) {
      if (raw[keys[i]] !== undefined && raw[keys[i]] !== null) return raw[keys[i]];
    }
    return null;
  };
  let changes = pick('changes', 'diff');
  if (typeof changes === 'string') {
    try { changes = JSON.parse(changes); } catch (e) { changes = {}; }
  }
  if (!changes || typeof changes !== 'object' || Array.isArray(changes)) changes = {};
  const at = pick('at', 'changedAt', 'timestamp', 'created_at');
  return {
    key: String(pick('id', 'auditId') || `row-${index}`),
    at: at ? new Date(at) : null,
    actor: pick('actor', 'actorEmail', 'user_email') || 'system',
    entityType: String(pick('entity_type', 'entityType') || ''),
    entityId: pick('entity_id', 'entityId') === null ? '' : String(pick('entity_id', 'entityId')),
    entityName: pick('entity_name', 'entityName') || '',
    action: String(pick('action', 'op') || 'UPDATE').toUpperCase(),
    changes,
  };
}

// Only fact-sheet rows move portfolio state. Relations, people and attachments
// are audited too but cannot change an application's disposition or completion.
const isFactSheetEntry = (e) => !e.entityType || /fact_?sheet/i.test(e.entityType);

const cloneApp = (fs) => ({
  ...fs,
  fields: { ...(fs.fields || {}) },
  tags: (fs.tags || []).slice(),
  relations: fs.relations || {},
});

// Write one historical value back onto a reconstructed fact sheet.
// The audit log speaks the database's language (snake_case columns, with all
// the flexible fields inside the `data` jsonb), the UI speaks the store's
// (`fields` holds everything) — this is the translation layer.
function applyHistoricValue(app, rawKey, value) {
  const key = camelize(rawKey);

  if (key === 'data' || key === 'fields') {
    // Whole-bag replacement: `from` is the complete jsonb as it stood. The DB
    // keeps name and lifecycle_state in their own columns, so anything the bag
    // does not carry must survive the swap.
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const keep = {};
      if (!('name' in value) && app.fields.name !== undefined) keep.name = app.fields.name;
      if (!('lifecycleState' in value) && app.fields.lifecycleState !== undefined) {
        keep.lifecycleState = app.fields.lifecycleState;
      }
      app.fields = { ...value, ...keep };
    }
    return;
  }
  if (key === 'tags' && Array.isArray(value)) { app.tags = value; return; }
  if (key === 'name') {
    if (value !== null && value !== undefined) {
      app.name = String(value);
      app.fields = { ...app.fields, name: app.name };
    }
    return;
  }
  if (TOP_LEVEL_KEYS[key]) { app[key] = value; return; }
  app.fields = { ...app.fields, [key]: value };
}

// Reconstruct the portfolio as of `cutoffMs` by reversing everything newer.
// `entries` MUST already be sorted newest-first.
function snapshotAt(currentList, entries, cutoffMs) {
  const state = {};
  currentList.forEach((fs) => { state[fs.id] = cloneApp(fs); });
  const stats = { reversed: 0, unmatched: 0, stubs: 0 };
  if (isNaN(cutoffMs)) return { list: Object.values(state), stats };

  entries.forEach((e) => {
    if (!e.at || isNaN(e.at.getTime()) || e.at.getTime() <= cutoffMs) return; // already "as of" the cutoff
    if (!e.entityId) return;
    stats.reversed += 1;

    if (e.action === 'INSERT') {
      // It did not exist yet.
      delete state[e.entityId];
      return;
    }
    if (e.action === 'DELETE') {
      // It still existed. The trigger logs no payload for a delete, so this is
      // a name-only stub — see limitation (2) at the top of the file.
      if (!state[e.entityId]) {
        stats.stubs += 1;
        state[e.entityId] = {
          id: e.entityId,
          type: 'Application',
          tenantId: '',
          name: e.entityName || '(deleted fact sheet)',
          fields: { name: e.entityName || '' },
          tags: [],
          relations: {},
          __stub: true,
        };
      }
      return;
    }

    const app = state[e.entityId];
    if (!app) { stats.unmatched += 1; return; } // an id we cannot place — never guess
    Object.keys(e.changes).forEach((field) => {
      const change = e.changes[field] || {};
      applyHistoricValue(app, field, change.from);
    });
  });

  return { list: Object.values(state), stats };
}

// ---- portfolio readings ---------------------------------------------------
const isApp = (fs) => fs.type === 'Application';
const waveOf = (a) => String((a.fields && a.fields.cmWave) || '').trim();
// 'Not Scheduled' is the LAST entry in the picklist on purpose: falling out of
// the schedule altogether is the worst possible slip, not a neutral move.
const waveRank = (w) => WAVES.indexOf(w);
const dispositionOf = (a) => String((a.fields && (a.fields.sixRClassification || a.fields.cmDisposition)) || '').trim()
  || 'Unclassified';
const isRetired = (a) => String((a.fields && a.fields.lifecycleState) || '') === 'End of Life'
  || String((a.fields && a.fields.cmCurrentStatus) || '') === 'Retired';
const completionOf = (a) => (a.__stub ? 0 : computeCompletion(a));
const average = (nums) => (nums.length ? Math.round(nums.reduce((x, y) => x + y, 0) / nums.length) : 0);

const BUCKETS = [
  { label: '0–24%', test: (c) => c < 25 },
  { label: '25–49%', test: (c) => c >= 25 && c < 50 },
  { label: '50–74%', test: (c) => c >= 50 && c < 75 },
  { label: '75–100%', test: (c) => c >= 75 },
];

function bucketCounts(apps) {
  return BUCKETS.map((b) => apps.filter((a) => b.test(completionOf(a))).length);
}

function dispositionMix(apps) {
  const counts = {};
  apps.forEach((a) => { const d = dispositionOf(a); counts[d] = (counts[d] || 0) + 1; });
  return counts;
}

// ---------------------------------------------------------------------------
// Presentational bits
// ---------------------------------------------------------------------------
function AppName({ app, onOpen }) {
  if (!onOpen || app.__stub) {
    return <span style={{ fontWeight: 600, color: NAVY }}>{app.name}</span>;
  }
  return (
    <Box component="span" onClick={() => onOpen(app.id)}
      sx={{ fontWeight: 600, color: BLUE, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
      {app.name}
    </Box>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
      <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, color: GREY, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 700, color: tone || NAVY, lineHeight: 1.2 }}>{value}</Typography>
      {sub ? <Typography sx={{ fontSize: 12, color: GREY }}>{sub}</Typography> : null}
    </Paper>
  );
}

function Section({ title, count, tone, hint, columns, rows, emptyText, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <Paper variant="outlined" sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.25, cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}>
        <IconButton size="small" sx={{ p: 0.25 }}>
          {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
        </IconButton>
        <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14 }}>{title}</Typography>
        <Chip size="small" label={count}
          sx={{ height: 20, fontWeight: 700, bgcolor: tone || '#EEF1F4', color: tone ? '#fff' : GREY }} />
        <Box sx={{ flex: 1 }} />
        {hint ? <Typography sx={{ fontSize: 12, color: GREY }}>{hint}</Typography> : null}
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Divider />
        {rows.length ? (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: HEAD, fontSize: 12 } }}>
                {columns.map((c) => <TableCell key={c}>{c}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.key || i} hover>
                  {r.cells.map((cell, j) => (
                    <TableCell key={j} sx={{ fontSize: 12.5 }}>{cell}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography sx={{ color: '#9AA6B2', fontSize: 13, p: 2 }}>{emptyText}</Typography>
        )}
      </Collapse>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
export default function SnapshotCompare({ tenantId, onOpen }) {
  const [fromDate, setFromDate] = useState(toInput(daysAgo(90)));
  const [toDate, setToDate] = useState(toInput(new Date()));
  const [current, setCurrent] = useState([]);
  const [entries, setEntries] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable('');
    // Current state first — it must render even if /history is missing.
    try {
      setCurrent(await store.listFactSheets(tenantId));
    } catch (e) {
      setCurrent([]);
    }
    try {
      const qs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
      const res = await fetch(`${getConfig().apiUrl}/history${qs}`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}` },
      });
      if (!res.ok) {
        setEntries([]);
        setUnavailable(res.status === 404
          ? 'The change-history API is not deployed in this environment yet, so no earlier snapshot can be reconstructed. The figures below are today\'s portfolio only. Comparison becomes available as soon as the /history endpoint is live.'
          : `The change-history API could not be reached (${res.status}). The figures below are today's portfolio only — no reconstruction of the earlier date was possible.`);
        return;
      }
      const text = await res.text();
      // An unparseable body is the API being unreachable in practice; saying
      // so beats rendering an empty history as though it were the truth.
      let data = [];
      try { data = text ? JSON.parse(text) : []; } catch (e) {
        setUnavailable('The change-history API returned a response that could not be read as JSON, so no earlier '
          + 'snapshot could be reconstructed. The figures below are today\'s portfolio only.');
        return;
      }
      const list = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
      setEntries(list.map(normalizeEntry).filter(isFactSheetEntry));
      // The old warning tested `entries.length >= 200` — but `entries` is what
      // survives the fact-sheet filter, so if 60 of the 200 rows were relation
      // or attachment entries the count was 140 and the warning never fired,
      // precisely when the reconstruction was least complete. The API now says
      // outright whether it truncated.
      setTruncated(Array.isArray(data) ? list.length >= 200 : !!data.hasMore);
      setTotalEntries(Array.isArray(data) ? list.length : (data.total ?? list.length));
    } catch (e) {
      setEntries([]);
      setUnavailable('The change-history API is not reachable from this environment, so no earlier snapshot could be reconstructed. The figures below are today\'s portfolio only.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Newest first — the reverse-replay depends on this ordering.
  const ordered = useMemo(
    () => entries.slice().sort((a, b) => (b.at ? b.at.getTime() : 0) - (a.at ? a.at.getTime() : 0)),
    [entries],
  );

  const auditStart = useMemo(() => {
    const dated = ordered.filter((e) => e.at && !isNaN(e.at.getTime()));
    return dated.length ? dated[dated.length - 1].at : null;
  }, [ordered]);

  const fromSnap = useMemo(() => snapshotAt(current, ordered, endOfDayMs(fromDate)), [current, ordered, fromDate]);
  const toSnap = useMemo(() => snapshotAt(current, ordered, endOfDayMs(toDate)), [current, ordered, toDate]);

  const fromApps = useMemo(() => fromSnap.list.filter(isApp), [fromSnap]);
  const toApps = useMemo(() => toSnap.list.filter(isApp), [toSnap]);

  const diff = useMemo(() => {
    const fromMap = new Map(fromApps.map((a) => [a.id, a]));
    const toMap = new Map(toApps.map((a) => [a.id, a]));

    const added = toApps.filter((a) => !fromMap.has(a.id));
    const removed = fromApps.filter((a) => !toMap.has(a.id));
    const retired = [];
    const dispositions = [];
    const waves = [];
    const ownership = [];
    const improved = [];

    toApps.forEach((to) => {
      const from = fromMap.get(to.id);
      if (!from) return;

      if (!isRetired(from) && isRetired(to)) {
        retired.push({ app: to, reason: String(to.fields.lifecycleState) === 'End of Life' ? 'Lifecycle set to End of Life' : 'Cloud status set to Retired' });
      }

      DISPOSITION_FIELDS.forEach((f) => {
        const a = (from.fields || {})[f.key];
        const b = (to.fields || {})[f.key];
        if (String(a || '') !== String(b || '')) dispositions.push({ app: to, label: f.label, from: a, to: b });
      });

      const wf = waveOf(from);
      const wt = waveOf(to);
      if (wf !== wt) {
        const rf = waveRank(wf);
        const rt = waveRank(wt);
        // Only a move BETWEEN two known waves can be called a slip; first-time
        // scheduling (blank -> Wave 2) is progress, not slippage.
        const later = rf >= 0 && rt >= 0 && rt > rf;
        waves.push({ app: to, from: wf, to: wt, later });
      }

      OWNERSHIP_FIELDS.forEach((f) => {
        const a = (from.fields || {})[f.key];
        const b = (to.fields || {})[f.key];
        if (String(a || '') !== String(b || '')) ownership.push({ app: to, label: f.label, from: a, to: b });
      });

      const cf = completionOf(from);
      const ct = completionOf(to);
      if (ct !== cf) improved.push({ app: to, from: cf, to: ct, delta: ct - cf });
    });

    // Anything that disappeared entirely also left the portfolio.
    removed.forEach((a) => retired.push({ app: a, reason: 'Fact sheet deleted' }));

    const fromAvg = average(fromApps.map(completionOf));
    const toAvg = average(toApps.map(completionOf));

    return {
      added,
      removed,
      retired,
      dispositions,
      waves,
      slipped: waves.filter((w) => w.later),
      ownership,
      improved: improved.filter((x) => x.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, 10),
      declined: improved.filter((x) => x.delta < 0).length,
      fromAvg,
      toAvg,
    };
  }, [fromApps, toApps]);

  // The line that gets pasted straight into the status report.
  const summary = useMemo(() => {
    const period = `Between ${longDate(fromDate, false)} and ${longDate(toDate, true)}`;
    const parts = [];
    parts.push(`${plural(diff.added.length, 'application')} added`);
    parts.push(`${diff.retired.length} retired`);
    const verb = diff.toAvg > diff.fromAvg ? 'rose' : (diff.toAvg < diff.fromAvg ? 'fell' : 'held');
    parts.push(diff.toAvg === diff.fromAvg
      ? `portfolio completion held at ${diff.toAvg}%`
      : `portfolio completion ${verb} from ${diff.fromAvg}% to ${diff.toAvg}%`);
    if (diff.slipped.length) parts.push(`${plural(diff.slipped.length, 'application')} slipped to a later wave`);
    return `${period}: ${sentenceList(parts)}.`;
  }, [fromDate, toDate, diff]);

  // ---- charts -------------------------------------------------------------
  const fromLabel = longDate(fromDate, false);
  const toLabel = longDate(toDate, false);

  const completionChart = useMemo(() => ({
    labels: BUCKETS.map((b) => b.label),
    datasets: [
      { label: fromLabel, data: bucketCounts(fromApps), backgroundColor: '#A9CDE8' },
      { label: toLabel, data: bucketCounts(toApps), backgroundColor: BLUE },
    ],
  }), [fromApps, toApps, fromLabel, toLabel]);

  const dispositionLabels = useMemo(() => {
    const seen = {};
    [...fromApps, ...toApps].forEach((a) => { seen[dispositionOf(a)] = true; });
    return Object.keys(seen).sort();
  }, [fromApps, toApps]);

  const donutFor = useCallback((apps) => {
    const counts = dispositionMix(apps);
    return {
      labels: dispositionLabels,
      datasets: [{
        data: dispositionLabels.map((l) => counts[l] || 0),
        backgroundColor: dispositionLabels.map((l, i) => (l === 'Unclassified' ? '#CFD8DE' : DONUT_COLORS[i % DONUT_COLORS.length])),
        borderWidth: 1,
        borderColor: '#fff',
      }],
    };
  }, [dispositionLabels]);

  const barOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } },
    scales: { y: { beginAtZero: true, title: { display: true, text: 'Applications' } } },
  };
  const donutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 10 } } } },
  };

  // ---- exports ------------------------------------------------------------
  const csvRows = useMemo(() => {
    const rows = [];
    diff.added.forEach((a) => rows.push({ category: 'Added', app: a.name, field: '', from: '', to: '' }));
    diff.retired.forEach((r) => rows.push({ category: 'Retired / removed', app: r.app.name, field: '', from: '', to: r.reason }));
    diff.dispositions.forEach((d) => rows.push({ category: 'Disposition change', app: d.app.name, field: d.label, from: displayValue(d.from), to: displayValue(d.to) }));
    diff.waves.forEach((w) => rows.push({ category: w.later ? 'Wave slip' : 'Wave move', app: w.app.name, field: 'CM: Selected for Wave', from: w.from || '—', to: w.to || '—' }));
    diff.improved.forEach((c) => rows.push({ category: 'Completion improvement', app: c.app.name, field: 'Completion %', from: `${c.from}%`, to: `${c.to}%` }));
    diff.ownership.forEach((o) => rows.push({ category: 'Ownership change', app: o.app.name, field: o.label, from: displayValue(o.from), to: displayValue(o.to) }));
    return rows;
  }, [diff]);

  const downloadCsv = () => exportCsv(
    `snapshot-compare-${fromDate}-to-${toDate}.csv`,
    csvRows,
    [
      { label: 'Category', value: 'category' },
      { label: 'Application', value: 'app' },
      { label: 'Field', value: 'field' },
      { label: 'From', value: 'from' },
      { label: 'To', value: 'to' },
    ],
  );

  // ---- notes the reader is entitled to ------------------------------------
  const notes = [];
  if (auditStart && endOfDayMs(fromDate) < auditStart.getTime()) {
    notes.push(`The audit trail available here starts on ${auditStart.toLocaleDateString()}. Anything last changed before that date cannot be reconstructed, so the "${fromLabel}" snapshot carries today's value for those fields.`);
  }
  if (fromSnap.stats.stubs || toSnap.stats.stubs) {
    notes.push('Deleted fact sheets are restored by name only — the audit trail records that a row was removed, not what it contained. They are counted as retired but their earlier field values are not asserted.');
  }
  if (fromSnap.stats.unmatched) {
    notes.push(`${fromSnap.stats.unmatched} audit entries refer to fact sheets that are not in the current inventory and could not be placed; they were ignored rather than guessed at.`);
  }
  if (truncated) {
    notes.push(totalEntries
      ? `Only the ${entries.length} most recent fact-sheet changes could be loaded, out of ${totalEntries} audit entries in total. Older changes are not part of this reconstruction.`
      : 'The history API returned only its most recent page of entries. Older changes beyond that limit are not part of this reconstruction.');
  }

  const badDates = isNaN(endOfDayMs(fromDate)) || isNaN(endOfDayMs(toDate))
    || endOfDayMs(fromDate) > endOfDayMs(toDate);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Snapshot compare</Typography>
        <Chip size="small" label="What moved since kickoff" sx={{ height: 20, bgcolor: '#EEF1F4', color: GREY }} />
        <Box sx={{ flex: 1 }} />
        <TextField size="small" type="date" label="From" value={fromDate}
          InputLabelProps={{ shrink: true }} onChange={(e) => setFromDate(e.target.value)} sx={{ width: 170 }} />
        <TextField size="small" type="date" label="To" value={toDate}
          InputLabelProps={{ shrink: true }} onChange={(e) => setToDate(e.target.value)} sx={{ width: 170 }} />
        <Tooltip title="Reload history">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
        <Button size="small" startIcon={<DownloadIcon />} onClick={downloadCsv}>CSV</Button>
        <Button size="small" startIcon={<PictureAsPdfIcon />}
          onClick={() => printElementToPdf(PRINT_ID, 'Portfolio Snapshot Compare')}>PDF</Button>
      </Stack>

      {unavailable ? <Alert severity="info" sx={{ mb: 2 }}>{unavailable}</Alert> : null}
      {badDates ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Pick two valid dates with “From” on or before “To”. Until then both snapshots read the same state.
        </Alert>
      ) : null}
      {!loading && !unavailable && !entries.length ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No change history has been recorded for this workspace yet, so there is nothing to reconstruct — both
          snapshots are today&apos;s portfolio. Snapshots build up from the point the audit trail starts: every create,
          update and delete from now on becomes a comparable point in time, and this page becomes useful within days.
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : (
        <div id={PRINT_ID}>
          {/* Charts are canvases; innerHTML cannot carry them into the print
              window, so they are hidden in the printed output rather than
              printing as empty boxes. */}
          <style>{`@media print { .sc-chart { display: none; } }`}</style>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderLeft: `4px solid ${BLUE}` }}>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, color: GREY, fontWeight: 700 }}>
              For the status report
            </Typography>
            <Typography sx={{ fontSize: 16, color: NAVY, fontWeight: 600, mt: 0.5 }}>{summary}</Typography>
            <Typography sx={{ fontSize: 12, color: GREY, mt: 1 }}>
              Reconstructed by reversing {fromSnap.stats.reversed} audit {fromSnap.stats.reversed === 1 ? 'entry' : 'entries'} against
              the current inventory of {plural(current.filter(isApp).length, 'application')}.
            </Typography>
          </Paper>

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <Stat label="Applications added" value={diff.added.length} tone={GREEN}
                sub={`${fromApps.length} → ${toApps.length} in portfolio`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Stat label="Retired / removed" value={diff.retired.length} tone={RED}
                sub={`${diff.removed.length} deleted outright`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Stat label="Completion" value={`${diff.fromAvg}% → ${diff.toAvg}%`}
                tone={diff.toAvg >= diff.fromAvg ? GREEN : RED}
                sub={`${diff.toAvg - diff.fromAvg >= 0 ? '+' : ''}${diff.toAvg - diff.fromAvg} points portfolio-wide`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Stat label="Slipped to a later wave" value={diff.slipped.length}
                tone={diff.slipped.length ? AMBER : GREY}
                sub={`${diff.waves.length} wave ${diff.waves.length === 1 ? 'change' : 'changes'} in total`} />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }} className="sc-chart">
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14, mb: 1 }}>
                  Completion profile — {fromLabel} vs {toLabel}
                </Typography>
                <Box sx={{ height: 260 }}><Bar data={completionChart} options={barOpts} /></Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14, mb: 1 }}>
                  Disposition mix (6R)
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 12, color: GREY, textAlign: 'center' }}>{fromLabel}</Typography>
                    <Box sx={{ height: 230 }}><Doughnut data={donutFor(fromApps)} options={donutOpts} /></Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 12, color: GREY, textAlign: 'center' }}>{toLabel}</Typography>
                    <Box sx={{ height: 230 }}><Doughnut data={donutFor(toApps)} options={donutOpts} /></Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          <Section
            title="Applications added"
            count={diff.added.length}
            tone={diff.added.length ? GREEN : null}
            hint="present today, absent at the From date"
            columns={['Application', 'Disposition', 'Wave', 'Completion']}
            emptyText="No applications were added in this window."
            rows={diff.added.map((a) => ({
              key: a.id,
              cells: [
                <AppName app={a} onOpen={onOpen} />,
                dispositionOf(a),
                waveOf(a) || '—',
                `${completionOf(a)}%`,
              ],
            }))}
          />

          <Section
            title="Applications retired or removed"
            count={diff.retired.length}
            tone={diff.retired.length ? RED : null}
            hint="end of life, retired, or deleted"
            columns={['Application', 'Why it left the active portfolio']}
            emptyText="No applications were retired or removed in this window."
            rows={diff.retired.map((r, i) => ({
              key: `${r.app.id}-${i}`,
              cells: [<AppName app={r.app} onOpen={onOpen} />, r.reason],
            }))}
          />

          <Section
            title="Disposition changes (6R / TIME)"
            count={diff.dispositions.length}
            hint="the decisions that moved"
            columns={['Application', 'Field', 'Was', 'Now']}
            emptyText="No disposition decisions changed in this window."
            rows={diff.dispositions.map((d, i) => ({
              key: `${d.app.id}-${d.label}-${i}`,
              cells: [
                <AppName app={d.app} onOpen={onOpen} />,
                d.label,
                <span style={{ color: GREY, textDecoration: 'line-through' }}>{displayValue(d.from)}</span>,
                <span style={{ color: NAVY, fontWeight: 600 }}>{displayValue(d.to)}</span>,
              ],
            }))}
          />

          <Section
            title="Wave movements"
            count={diff.waves.length}
            tone={diff.slipped.length ? AMBER : null}
            hint={diff.slipped.length ? `${diff.slipped.length} slipped later` : 'none slipped later'}
            defaultOpen={!!diff.slipped.length}
            columns={['Application', 'Was', 'Now', 'Direction']}
            emptyText="No applications changed migration wave in this window."
            rows={diff.waves
              .slice()
              .sort((a, b) => Number(b.later) - Number(a.later))
              .map((w, i) => ({
                key: `${w.app.id}-${i}`,
                cells: [
                  <AppName app={w.app} onOpen={onOpen} />,
                  w.from || '—',
                  w.to || '—',
                  w.later
                    ? <Chip size="small" label="SLIPPED LATER"
                      sx={{ height: 20, fontSize: 11, fontWeight: 700, color: '#7A4A00', bgcolor: '#FFF3E0', border: '1px solid #FFCC80' }} />
                    : <span style={{ color: GREY }}>{w.from ? 'moved' : 'newly scheduled'}</span>,
                ],
              }))}
          />

          <Section
            title="Most-improved completion"
            count={diff.improved.length}
            hint={`portfolio average ${diff.fromAvg}% → ${diff.toAvg}%${diff.declined ? ` · ${diff.declined} went backwards` : ''}`}
            columns={['Application', 'Was', 'Now', 'Gain']}
            emptyText="No application gained completion in this window."
            rows={diff.improved.map((c) => ({
              key: c.app.id,
              cells: [
                <AppName app={c.app} onOpen={onOpen} />,
                `${c.from}%`,
                `${c.to}%`,
                <span style={{ color: GREEN, fontWeight: 700 }}>+{c.delta}</span>,
              ],
            }))}
          />

          <Section
            title="Ownership changes"
            count={diff.ownership.length}
            hint="business, technical and stakeholder owners"
            columns={['Application', 'Role', 'Was', 'Now']}
            emptyText="No ownership changed in this window."
            rows={diff.ownership.map((o, i) => ({
              key: `${o.app.id}-${o.label}-${i}`,
              cells: [
                <AppName app={o.app} onOpen={onOpen} />,
                o.label,
                <span style={{ color: GREY, textDecoration: 'line-through' }}>{displayValue(o.from)}</span>,
                <span style={{ color: NAVY, fontWeight: 600 }}>{displayValue(o.to)}</span>,
              ],
            }))}
          />

          {notes.length ? (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 2, bgcolor: '#FAFBFC' }}>
              <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, color: GREY, fontWeight: 700, mb: 0.5 }}>
                How to read this
              </Typography>
              {notes.map((n, i) => (
                <Typography key={i} sx={{ fontSize: 12, color: GREY, py: 0.25 }}>• {n}</Typography>
              ))}
            </Paper>
          ) : null}
        </div>
      )}
    </Box>
  );
}
